import React, { useEffect, useRef, useState } from "react";
import { UserInfo } from "./model/backendTypes";
import { EMPTY_TEMPLATE, MEDICAL_TEMPLATE, SUDOKU_TEMPLATE, TEACHING_TEMPLATE } from "./templates";

type Props = {
  user: UserInfo | null;
  onSelectTemplate: (templateName: string, templateXml: string) => void;
  disabled?: boolean;
};

export default function ProjectNewDropdown({ user, onSelectTemplate, disabled }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | undefined>(undefined);

  const positionDropdown = React.useCallback(() => {
    const root = rootRef.current;
    const dropdown = dropdownRef.current;
    if (!root || !dropdown) return;

    const anchorRect = root.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 8;
    const width = 250;

    let left = anchorRect.left;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - viewportPadding - width));

    const measuredHeight = dropdown.getBoundingClientRect().height || 300;
    const availableBelow = window.innerHeight - (anchorRect.bottom + gap) - viewportPadding;
    const availableAbove = anchorRect.top - gap - viewportPadding;
    const shouldOpenUpward = availableBelow < Math.min(measuredHeight, 250) && availableAbove > availableBelow;

    let top: number;
    let maxHeight: number;

    if (shouldOpenUpward) {
      maxHeight = Math.max(180, availableAbove);
      const preferredTop = anchorRect.top - gap - Math.min(measuredHeight, maxHeight);
      top = Math.max(viewportPadding, preferredTop);
    } else {
      top = anchorRect.bottom + gap;
      maxHeight = Math.max(180, availableBelow);
    }

    setDropdownStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight: Math.min(maxHeight, 400),
      zIndex: 2000,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setDropdownStyle(undefined);
      return;
    }

    let frame = 0;
    const syncPosition = () => {
      cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(positionDropdown);
    };

    syncPosition();
    window.addEventListener("resize", syncPosition);
    window.addEventListener("scroll", syncPosition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncPosition);
      window.removeEventListener("scroll", syncPosition, true);
    };
  }, [open, positionDropdown]);

  // Event-Listener zum Schließen des Dropdowns bei:
  // - Klicks außerhalb des Komponenten-Containers (rootRef)
  // - Drücken der Escape-Taste
  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node;
      if (!rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handleSelect = (name: string, xml: string) => {
    onSelectTemplate(name, xml);
    setOpen(false);
  };

  const renderTemplateButton = (t: { id: string; name: string; xml: string; icon: string; desc: string }) => (
    <button
      key={t.id}
      type="button"
      className="profile-menu__template-btn"
      onClick={() => handleSelect(t.name, t.xml)}
      title={`Create new project from '${t.name}' template`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px", overflow: "hidden" }}>
        <div className="profile-menu__template-icon">
          {t.icon}
        </div>
        
        <div className="profile-menu__template-content">
          <span className="profile-menu__template-name">
            {t.name}
          </span>
          <span className="profile-menu__template-desc">
            {t.desc}
          </span>
        </div>
      </div>
    </button>
  );

  return (
    <div className="profile-menu" ref={rootRef} style={{ display: "inline-block" }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled || !user}
      >
        new
      </button>

      {open && (
        <div 
          className="profile-menu__dropdown profile-menu__dropdown--compact" 
          role="menu"
          ref={dropdownRef}
          style={dropdownStyle}
        >
          <div className="profile-menu__section-title" style={{ marginTop: 0 }}>Create Project</div>
          
          <div 
            className="profile-menu__submenu" 
            style={{ 
              maxHeight: "260px",
              overflowY: "auto", 
              paddingRight: "4px" 
            }}
          >
            <div className="profile-menu__subsection-title" style={{ paddingTop: "8px" }}>General</div>
            {[
              { id: "empty", name: "Empty Project", xml: EMPTY_TEMPLATE, icon: "📄", desc: "Blank project" }
            ].map(renderTemplateButton)}

            <div className="profile-menu__subsection-title">Medical</div>
            {[
              { id: "medical", name: "Basic Medical", xml: MEDICAL_TEMPLATE, icon: "🏥", desc: "Medical planning" }
            ].map(renderTemplateButton)}

            <div className="profile-menu__subsection-title">Teaching</div>
            {[
              { id: "teaching", name: "Basic Teaching", xml: TEACHING_TEMPLATE, icon: "🏫", desc: "Class schedule" },
            ].map(renderTemplateButton)}

            <div className="profile-menu__subsection-title">Other</div>
            {[
              { id: "sudoku", name: "Sudoku", xml: SUDOKU_TEMPLATE, icon: "🧩", desc: "9x9 Sudoku format" }
            ].map(renderTemplateButton)}
          </div>


        </div>
      )}
    </div>
  );
}
